import React, { useCallback, useEffect, useState } from 'react';
import { authHeaders } from './Admin';

import { API_BASE } from '../apiBase';

const C = {
  panel: '#141416',
  panelAlt: '#1a1a1d',
  border: '#262629',
  text: '#f2f2f3',
  sub: '#8a8a90',
  accent: '#2D9E5F',
};

export interface BackupItem {
  key: string;
  size_bytes: number;
  uploaded_at: string;
  age_hours: number;
}

export interface BackupStatus {
  status: 'ok' | 'warning' | 'failed' | 'unconfigured';
  message: string;
  latest: BackupItem | null;
  daily_count: number;
  total_bytes: number;
  archive_kept: number;
  daily: BackupItem[];
  archive: BackupItem[];
}

export const STATUS_COLOR: Record<string, string> = {
  ok: '#34D399',
  warning: '#FBBF24',
  failed: '#F87171',
  unconfigured: '#8a8a90',
};

export function fetchBackupStatus(token: string): Promise<BackupStatus> {
  return fetch(`${API_BASE}/admin/backups`, { headers: authHeaders(token) })
    .then((r) => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    })
    .then((d) => (d.data ?? d) as BackupStatus);
}

export function mb(bytes: number): string {
  if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(2)} MB`;
  return `${(bytes / 1024).toFixed(0)} KB`;
}

function when(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
    timeZone: 'Asia/Karachi',
  });
}

function ago(hours: number): string {
  if (hours < 1) return `${Math.round(hours * 60)} min ago`;
  if (hours < 48) return `${Math.round(hours)} hours ago`;
  return `${Math.round(hours / 24)} days ago`;
}

function BackupsTab({ token, isMobile }: { token: string; isMobile: boolean }) {
  const [st, setSt] = useState<BackupStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setSt(await fetchBackupStatus(token));
    } catch (e: any) {
      setError(e.message || 'Failed to load');
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const th: React.CSSProperties = {
    textAlign: 'right', padding: '11px 12px', fontSize: 11.5, color: C.sub,
    fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4,
  };
  const td: React.CSSProperties = {
    textAlign: 'right', padding: '11px 12px', fontSize: 13.5, color: C.text,
    borderTop: `1px solid ${C.border}`, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
  };
  const card: React.CSSProperties = {
    background: C.panel, border: `1px solid ${C.border}`, borderRadius: 14, padding: '18px 20px',
  };

  const color = st ? STATUS_COLOR[st.status] : C.sub;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 6 }}>
        <h1 style={{ fontSize: 28, fontWeight: 800, margin: 0, color: C.text }}>Backups</h1>
        <button
          onClick={load}
          style={{ marginLeft: 'auto', background: C.panelAlt, border: `1px solid ${C.border}`, borderRadius: 8, padding: '9px 18px', cursor: 'pointer', color: C.text, fontSize: 14 }}
        >
          Refresh
        </button>
      </div>
      <p style={{ color: C.sub, marginTop: 0, marginBottom: 18, fontSize: 14.5, lineHeight: 1.6 }}>
        The database is dumped nightly at 03:30 Pakistan time and uploaded to the private
        <code style={{ color: C.text, margin: '0 5px' }}>gardeno-backups</code>
        bucket on Cloudflare R2. Nothing is deleted automatically yet.
      </p>

      {error && <div style={{ color: '#F87171', fontSize: 13.5, marginBottom: 12 }}>{error}</div>}
      {loading && !st && <div style={{ color: C.sub, fontSize: 13.5 }}>Loading…</div>}

      {st && (
        <>
          <div style={{ ...card, borderLeft: `4px solid ${color}`, marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: color, display: 'inline-block' }} />
              <strong style={{ fontSize: 16, color: C.text }}>
                {st.status === 'ok' ? 'Backups healthy'
                  : st.status === 'warning' ? 'Backup overdue'
                  : st.status === 'failed' ? 'Backups failing'
                  : 'Not configured'}
              </strong>
              <span style={{ color: C.sub, fontSize: 14 }}>{st.message}</span>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
            {[
              { label: 'Latest backup', value: st.latest ? ago(st.latest.age_hours) : 'never', color },
              { label: 'Backups stored', value: String(st.daily_count) },
              { label: 'Latest size', value: st.latest ? mb(st.latest.size_bytes) : '—' },
              { label: 'Total in bucket', value: mb(st.total_bytes) },
            ].map((k) => (
              <div key={k.label} style={card}>
                <div style={{ color: C.sub, fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>{k.label}</div>
                <div style={{ fontSize: 22, fontWeight: 800, marginTop: 8, color: k.color || C.text }}>{k.value}</div>
              </div>
            ))}
          </div>

          <h2 style={{ fontSize: 17, fontWeight: 800, margin: '0 0 10px' }}>Recent backups</h2>
          <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 14, overflowX: 'auto', marginBottom: 22 }}>
            <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 520 }}>
              <thead>
                <tr>
                  <th style={{ ...th, textAlign: 'left' }}>File</th>
                  <th style={th}>Uploaded</th>
                  <th style={th}>Age</th>
                  <th style={th}>Size</th>
                </tr>
              </thead>
              <tbody>
                {st.daily.map((it, i) => (
                  <tr key={it.key} style={{ background: i === 0 ? C.panelAlt : 'transparent' }}>
                    <td style={{ ...td, textAlign: 'left', fontWeight: i === 0 ? 700 : 400 }}>
                      {it.key.replace('daily/', '')}
                      {i === 0 && <span style={{ marginLeft: 8, fontSize: 10.5, color: C.accent, fontWeight: 800 }}>LATEST</span>}
                    </td>
                    <td style={td}>{when(it.uploaded_at)}</td>
                    <td style={td}>{ago(it.age_hours)}</td>
                    <td style={td}>{mb(it.size_bytes)}</td>
                  </tr>
                ))}
                {st.daily.length === 0 && (
                  <tr><td colSpan={4} style={{ ...td, textAlign: 'center', color: C.sub }}>No backups yet</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {st.archive.length > 0 && (
            <>
              <h2 style={{ fontSize: 17, fontWeight: 800, margin: '0 0 6px' }}>Archived data</h2>
              <p style={{ color: C.sub, fontSize: 13.5, margin: '0 0 10px', lineHeight: 1.6 }}>
                Static data kept out of the nightly dumps so it is not re-uploaded every night.
                Never expires.
              </p>
              <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 14, overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 520 }}>
                  <tbody>
                    {st.archive.map((it) => (
                      <tr key={it.key}>
                        <td style={{ ...td, textAlign: 'left', borderTop: 'none' }}>{it.key.replace('archive/', '')}</td>
                        <td style={{ ...td, borderTop: 'none' }}>{when(it.uploaded_at)}</td>
                        <td style={{ ...td, borderTop: 'none' }}>{mb(it.size_bytes)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

export default BackupsTab;
